// Office adapter — reads from Office.js (client-side) and optionally enriches
// the result with EPIC data via GraphQL. The resolution call is best-effort:
// if it fails or no token is provided, we return what Office.js gave us with
// `inEpic = false` for everyone.

import { resolveContacts } from "./api/client";
import type { TokenRefresh } from "./api/graphql";
import { applyResolution } from "./api/mappers";
import type { Connection, ContactEntity, EmailContext } from "./types";

/* global Office, console */

export const OfficeAdapter = {
  async ready(): Promise<void> {
    // Office.onReady is already called in taskpane.tsx, so this is a no-op.
  },

  /**
   * Read the open Outlook message and (when authenticated) enrich the
   * sender + recipients with EPIC contact data via Query.resolve_contacts.
   */
  async getEmailContext(
    token?: string | null,
    refresh?: TokenRefresh
  ): Promise<EmailContext> {
    const item = (Office.context?.mailbox as any)?.item;
    if (!item) throw new Error("No mailbox item");

    // Read mode: from/to/cc are arrays. Compose mode has them as Recipients
    // objects with .getAsync(); not yet supported here.
    if (!Array.isArray(item.to)) {
      throw new Error("Compose mode not yet supported");
    }

    const subject: string = item.subject || "(no subject)";
    const itemId: string = item.itemId || "live-item";
    const internetMessageId: string | undefined =
      typeof item.internetMessageId === "string" ? item.internetMessageId : undefined;
    const receivedAt: string =
      item.dateTimeCreated instanceof Date
        ? item.dateTimeCreated.toISOString()
        : new Date().toISOString();

    const ctx: EmailContext = {
      itemId,
      internetMessageId,
      subject,
      from: contactFromOffice(item.from),
      to: (item.to || []).map((r: any) => contactFromOffice(r)),
      cc: (item.cc || []).map((r: any) => contactFromOffice(r)),
      receivedAt,
      bodyPreview: "",
    };

    if (token) {
      const all = [ctx.from, ...ctx.to, ...ctx.cc];
      const uniqueEmails = [...new Set(all.map((c) => c.email).filter((e) => !!e))];
      if (uniqueEmails.length > 0) {
        try {
          const resolutions = await resolveContacts(uniqueEmails, token, refresh);
          const byEmail = new Map(resolutions.map((r) => [r.email.toLowerCase(), r]));
          ctx.from = applyResolution(ctx.from, byEmail.get(ctx.from.email));
          ctx.to = ctx.to.map((c) => applyResolution(c, byEmail.get(c.email)));
          ctx.cc = ctx.cc.map((c) => applyResolution(c, byEmail.get(c.email)));
        } catch (e) {
          console.warn("EPIC resolve_contacts failed:", e);
        }
      }
    }

    return ctx;
  },

  /** Stubbed pending the write-path wire-up. */
  async createConnection(input: {
    itemId: string;
    entityId: string;
    role?: string;
    remarks?: string;
  }): Promise<Connection> {
    throw new Error(
      `Stub: createConnection not yet wired (input=${JSON.stringify(input)})`
    );
  },
};

/** Connections list — real query lives in App via fetchConnectionsForEmail. */
export const initialConnectionsForEmail = (_itemId: string): Connection[] => [];

function contactFromOffice(
  officeRecipient: { emailAddress?: string; displayName?: string } | undefined | null
): ContactEntity {
  const email = (officeRecipient?.emailAddress || "").toLowerCase();
  const displayName = officeRecipient?.displayName || email || "(unknown)";
  return {
    id: email ? `live-${email}` : `live-${Math.random().toString(36).slice(2, 8)}`,
    kind: "contact",
    title: displayName,
    email,
    inEpic: false,
    initials: initialsFor(displayName),
    palette: paletteFor(email || displayName),
  };
}

function initialsFor(name: string): string {
  const cleaned = name.trim();
  if (!cleaned) return "?";
  const parts = cleaned.split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function paletteFor(seed: string): "a" | "b" | "c" | "d" {
  let hash = 0;
  for (let i = 0; i < seed.length; i++) {
    hash = (hash << 5) - hash + seed.charCodeAt(i);
    hash |= 0;
  }
  return (["a", "b", "c", "d"] as const)[Math.abs(hash) % 4];
}
